<script setup>
import { onMounted, watch, ref } from 'vue';
import { useProjeto } from '@/modules/projeto/composables/useProjeto';
import { formataCpf, validarCNPJ } from '@/commons/utils';

const { membros, carregarMembrosPagamento, carregarSolicitacoesNaoPaginada, solicitacoes } = useProjeto();

const props = defineProps({
    projetoId: String,
    erro: {
        type: String,
        default: null
    },
    modelValue: {
        type: Number,
        default: null
    },
    tipo_solicitacao_id: {
        type: Number,
        default: null
    }
})

const emit = defineEmits(['update:modelValue', 'change']);

const membroSelecionado = ref(null);

watch(() => props.modelValue, async (newValue) => {
    if (newValue) {
        await carregarMembrosPagamento(props.projetoId);

        const membro = membros.value.find(m => m.id === newValue);
        if (membro && membroSelecionado.value?.id !== newValue) {
            membroSelecionado.value = membro;
            
            emit('change', membro);
        }
    } else if (membroSelecionado.value !== null) {
        membroSelecionado.value = null;
    }
}, { immediate: true });

// Watch para carregar membros quando projetoId mudar
watch(
    () => props.projetoId,
    async (newProjetoId) => {
        if (newProjetoId) {
            await carregarMembrosPagamento(newProjetoId);
            // Sincronizar com modelValue após carregar membros
            if (props.modelValue) {
                const membro = membros.value.find(m => m.id === props.modelValue) || null;
                membroSelecionado.value = membro;
                if (membro) {
                    emit('change', membro);
                }
            }
        } else {
            membroSelecionado.value = null;
        }
    }
);

// Watch para emitir mudanças quando usuário seleciona um membro
watch(membroSelecionado, async (newMembro) => {
    const newValue = newMembro?.id || null;
    if (newValue !== props.modelValue) {
        if (!newMembro) {
            emit('update:modelValue', null);
            emit('change', null, []);
            return;
        }

        await carregarSolicitacoesNaoPaginada(props.projetoId,{ tipo_solicitacao_id: props.tipo_solicitacao_id, membro_id: newMembro.id, validas: true , size: 100});
            
        emit('update:modelValue', newValue);
        emit('change', newMembro, solicitacoes.value);
    }
});

onMounted(async () => {
    // Aguardar carregamento inicial dos membros
    if (props.projetoId) {
        await carregarMembrosPagamento(props.projetoId);
    }
});
</script>

<template>
    <div class="field mb-2 col-12">
        <SmallTitle titulo="Membro" :obrigatorio="true" />
        <Dropdown :options="membros" v-model="membroSelecionado" optionLabel="nome"
            emptyFilterMessage="Nenhum membro encontrado" :class="{ 'p-invalid': props.erro }"
            placeholder="Selecione o membro" :showClear="true">

            <template #value="slotProps">
                <div v-if="slotProps.value" class="flex align-items-center">
                    <div class="flex flex-column gap-1">
                        <p class="font-semibold text-sm text-800 m-0">
                            {{ slotProps.value.nome }}
                        </p>
                        <p class="text-xs m-0 text-500 font-normal">
                            {{ formataCpf(slotProps.value.cpf, false) }}</p>
                    </div>
                </div>
                <span v-else>
                    <div class=" text-sm">
                        {{ slotProps.placeholder }}
                    </div>
                </span>
            </template>

            <template #option="slotProps">
                <div class="flex flex-column gap-1">
                    <p class="font-semibold text-sm m-0">
                        {{ slotProps.option.nome }}
                    </p>
                    <p class="text-xs m-0 text-500 font-normal">{{ formataCpf(slotProps.option.cpf, false) }}
                    </p>
                </div>
            </template>
        </Dropdown>
        <SmallError v-if="props.erro" :error="props.erro" />
    </div>
</template>